import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, Navigate } from 'react-router';
import { RequireCustomer, RequirePlatformAdmin, RequireRole, RequireStaff, RequireUser } from './guards.js';

/**
 * Route-level code splitting is mandatory — AGENTS.md §7.
 *
 * "A barista on a cheap Android phone on a slow connection must not download
 * the analytics dashboard bundle." Every surface is lazy, so `/staff` pulls the
 * counter screens and nothing else. The bundle budget in CI is what keeps this
 * honest; a static import added later would silently undo it.
 */
const StaffLogin = lazy(() => import('../surfaces/pos/StaffLogin.js'));
const CounterHome = lazy(() => import('../surfaces/pos/CounterHome.js'));

const MerchantLogin = lazy(() => import('../surfaces/dashboard/MerchantLogin.js'));
const MerchantSignup = lazy(() => import('../surfaces/dashboard/MerchantSignup.js'));
const ForgotPassword = lazy(() => import('../surfaces/dashboard/ForgotPassword.js'));
const ResetPassword = lazy(() => import('../surfaces/dashboard/ResetPassword.js'));
const DashboardLayout = lazy(() => import('../surfaces/dashboard/DashboardLayout.js'));
const DashboardHome = lazy(() => import('../surfaces/dashboard/DashboardHome.js'));
const CustomerList = lazy(() => import('../surfaces/dashboard/CustomerList.js'));
const FraudReport = lazy(() => import('../surfaces/dashboard/FraudReport.js'));
const BrandSettings = lazy(() => import('../surfaces/dashboard/BrandSettings.js'));
const PlanUsage = lazy(() => import('../surfaces/dashboard/PlanUsage.js'));
const InvoiceDetail = lazy(() => import('../surfaces/dashboard/InvoiceDetail.js'));
const ProgramEditor = lazy(() => import('../surfaces/dashboard/ProgramEditor.js'));
const RewardsManager = lazy(() => import('../surfaces/dashboard/RewardsManager.js'));
const WinBackCampaign = lazy(() => import('../surfaces/dashboard/WinBackCampaign.js'));
const NewCampaign = lazy(() => import('../surfaces/dashboard/NewCampaign.js'));
const CampaignResults = lazy(() => import('../surfaces/dashboard/CampaignResults.js'));
const Automations = lazy(() => import('../surfaces/dashboard/Automations.js'));
const ReferralSettings = lazy(() => import('../surfaces/dashboard/ReferralSettings.js'));
const Campaigns = lazy(() => import('../surfaces/dashboard/Campaigns.js'));
const Account = lazy(() => import('../surfaces/dashboard/Account.js'));
const TeamManagement = lazy(() => import('../surfaces/dashboard/TeamManagement.js'));
const AcceptInvite = lazy(() => import('../surfaces/dashboard/AcceptInvite.js'));
const Setup = lazy(() => import('../surfaces/dashboard/Setup.js'));
const GiftCards = lazy(() => import('../surfaces/dashboard/GiftCards.js'));
const Memberships = lazy(() => import('../surfaces/dashboard/Memberships.js'));
const PlatformLogin = lazy(() => import('../surfaces/platform/PlatformLogin.js'));
const ChurnRisk = lazy(() => import('../surfaces/platform/ChurnRisk.js'));
const PlatformLayout = lazy(() => import('../surfaces/platform/PlatformLayout.js'));
const ImpersonationHistory = lazy(() => import('../surfaces/platform/ImpersonationHistory.js'));
const OrganizationBilling = lazy(() => import('../surfaces/platform/OrganizationBilling.js'));

const CustomerLogin = lazy(() => import('../surfaces/customer/CustomerLogin.js'));
const CustomerLayout = lazy(() => import('../surfaces/customer/CustomerLayout.js'));
const MyCards = lazy(() => import('../surfaces/customer/MyCards.js'));
const CustomerProfile = lazy(() => import('../surfaces/customer/CustomerProfile.js'));
const PublicCard = lazy(() => import('../surfaces/customer/PublicCard.js'));
const GiftCardClaim = lazy(() => import('../surfaces/customer/GiftCardClaim.js'));
const CardDetail = lazy(() => import('../surfaces/customer/CardDetail.js'));
const ReferralJoin = lazy(() => import('../surfaces/customer/ReferralJoin.js'));

/**
 * The fallback is intentionally plain text rather than a skeleton.
 *
 * A skeleton that flashes for 80ms on a fast connection is noise; on the slow
 * connection this app is built for, a skeleton that never resolves is worse
 * than a line that says what is happening.
 */
function RouteFallback() {
  return (
    <div className="grid min-h-dvh place-items-center" role="status" aria-live="polite">
      <span className="text-fg-muted">Loading…</span>
    </div>
  );
}

function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<RouteFallback />}>{children}</Suspense>;
}

export const router = createBrowserRouter([
  // `/` is not a surface. Merchants land on the dashboard; customers arrive
  // through a signed link and never see this.
  { path: '/', element: <Navigate to="/dashboard" replace /> },

  /**
   * Platform admin — features/PLATFORM-ADMIN.md. A fourth surface, which AGENTS.md §7's
   * table did not have; the table is updated in the same change.
   *
   * Lazy like every other route, so a barista never downloads it. Its own
   * login is separate from the merchant's, so there is no shared layout to
   * hang these under.
   */
  { path: '/platform/login', element: <Lazy><PlatformLogin /></Lazy> },
  {
    path: '/platform',
    element: (
      <RequirePlatformAdmin>
        <Lazy><PlatformLayout /></Lazy>
      </RequirePlatformAdmin>
    ),
    children: [
      { index: true, element: <Lazy><ChurnRisk /></Lazy> },
      // Who opened which merchant's account, and why — PLAT-1.
      { path: 'impersonations', element: <Lazy><ImpersonationHistory /></Lazy> },
      { path: 'organizations/:organizationId', element: <Lazy><OrganizationBilling /></Lazy> },
    ],
  },

  // ── Merchant dashboard: OWNER, MANAGER ──────────────────────
  { path: '/login', element: <Lazy><MerchantLogin /></Lazy> },
  { path: '/signup', element: <Lazy><MerchantSignup /></Lazy> },
  { path: '/forgot-password', element: <Lazy><ForgotPassword /></Lazy> },
  { path: '/reset-password', element: <Lazy><ResetPassword /></Lazy> },
  // A manager's invitation — public, they have no account yet (features/TEAM.md).
  { path: '/invite', element: <Lazy><AcceptInvite /></Lazy> },
  {
    path: '/dashboard',
    element: <RequireUser />,
    children: [
      {
        element: <Lazy><DashboardLayout /></Lazy>,
        children: [
          { index: true, element: <Lazy><DashboardHome /></Lazy> },
          { path: 'customers', element: <Lazy><CustomerList /></Lazy> },
          // Managers and staff share one page — features/TEAM.md. A manager runs
          // their location's staff and devices there; the managers section is the owner's.
          { path: 'team', element: <Lazy><TeamManagement /></Lazy> },
          // Staff had its own page until 2026-10-12; old links and bookmarks still land.
          { path: 'staff', element: <Navigate to="/dashboard/team" replace /> },
          { path: 'account', element: <Lazy><Account /></Lazy> },
          // features/GIFT-CARDS.md. Owner and manager both: a manager sees the
          // cards sold at their location and the receipts rung up on its till.
          { path: 'gift-cards', element: <Lazy><GiftCards /></Lazy> },
          /**
           * The owner's screens. A manager runs one location and these define
           * the business — features/TEAM.md. The server refuses them too; this
           * only spares a manager who follows an old link a page of 403s.
           */
          {
            element: <RequireRole roles={['OWNER']} />,
            children: [
              { path: 'programme', element: <Lazy><ProgramEditor /></Lazy> },
              { path: 'rewards', element: <Lazy><RewardsManager /></Lazy> },
              { path: 'activity', element: <Lazy><FraudReport /></Lazy> },
              { path: 'settings', element: <Lazy><BrandSettings /></Lazy> },
              { path: 'plan', element: <Lazy><PlanUsage /></Lazy> },
              // One invoice in full, from the Plan page's list — BILL-5.
              { path: 'plan/invoices/:invoiceId', element: <Lazy><InvoiceDetail /></Lazy> },
              // features/CAMPAIGNS.md's flagship flow: the "gone quiet" card leads straight here.
              { path: 'campaigns', element: <Lazy><Campaigns /></Lazy> },
              { path: 'campaigns/win-back', element: <Lazy><WinBackCampaign /></Lazy> },
              // Any campaign message, any audience — MSG-8 / MSG-3.
              { path: 'campaigns/new', element: <Lazy><NewCampaign /></Lazy> },
              { path: 'campaigns/:campaignId', element: <Lazy><CampaignResults /></Lazy> },
              { path: 'automations', element: <Lazy><Automations /></Lazy> },
              // Its own page, not an automation — NET-6.
              { path: 'referrals', element: <Lazy><ReferralSettings /></Lazy> },
              // features/MEMBERSHIPS.md — the page says so itself on a brand without it.
              { path: 'memberships', element: <Lazy><Memberships /></Lazy> },
            ],
          },
        ],
      },
      // Outside the layout: setup has its own progress header, and the nav
      // would offer links to screens that do not work yet.
      {
        element: <RequireRole roles={['OWNER']} />,
        children: [{ path: 'setup', element: <Lazy><Setup /></Lazy> }],
      },
    ],
  },

  // ── Staff counter app: STAFF ────────────────────────────────
  { path: '/staff/login', element: <Lazy><StaffLogin screen="login" /></Lazy> },
  { path: '/staff/pair', element: <Lazy><StaffLogin screen="pair" /></Lazy> },
  {
    path: '/staff',
    element: <RequireStaff />,
    children: [{ index: true, element: <Lazy><CounterHome /></Lazy> }],
  },

  // ── Customer card ───────────────────────────────────────────
  { path: '/c/login', element: <Lazy><CustomerLogin /></Lazy> },
  {
    path: '/c',
    // The tab bar — Cards, the code, Profile — around every signed-in page.
    element: (
      <RequireCustomer>
        <Lazy><CustomerLayout /></Lazy>
      </RequireCustomer>
    ),
    children: [
      { index: true, element: <Lazy><MyCards /></Lazy> },
      // One card, its history and its referral link — CARD-1.
      { path: 'cards/:brandId', element: <Lazy><CardDetail /></Lazy> },
      { path: 'profile', element: <Lazy><CustomerProfile /></Lazy> },
    ],
  },

  /**
   * The public card. Unauthenticated by design — features/CUSTOMER-CARD.md requires it to
   * work with no account, because a customer who has to sign in at the counter
   * to show their card is a customer who stops using the card.
   */
  { path: '/p/:passSlug', element: <Lazy><PublicCard /></Lazy> },

  /**
   * A gift card's claim link — features/GIFT-CARDS.md (`GIFT-4`). Public, like
   * the pass link: the recipient has no account yet. It shows what the card is
   * and sends them to sign in with the number it was sent to.
   */
  { path: '/g/:code', element: <Lazy><GiftCardClaim /></Lazy> },

  /**
   * A friend's referral link — features/REFERRALS.md (`NET-7`). Public, like the
   * claim link: the person opening it may have no account. Joining needs one.
   */
  { path: '/r/:code', element: <Lazy><ReferralJoin /></Lazy> },

  { path: '*', element: <NotFound /> },
]);

function NotFound() {
  return (
    <main className="grid min-h-dvh place-items-center p-8 text-center">
      <div>
        <h1 className="text-xl font-semibold text-fg">Page not found</h1>
        <a href="/dashboard" className="mt-2 inline-block text-accent underline">
          Go to the dashboard
        </a>
      </div>
    </main>
  );
}
